import { cloneDeep, isArray, isNumber } from 'lodash';
import { IShape } from '@/utils/collision-kit';
import { SizeUnitType } from '@/utils/unit-kit';
import { CanvasType, ILayerProps, StageType } from '@/version-types';
import { CoreAPI, parseLayerId } from '../../core';
import { getStageState } from '../states/stage-state';
import { checkIsLongImageMode } from '../utils/node-kit';
import { StageBase } from './stage-base';

export class StageData extends StageBase {
    constructor(identity: string) {
        super(identity);
    }

    destroy() {
        getStageState(this.identity).destroy();
        super.destroy();
    }

    /** 视口宽度 */
    getViewWidth() {
        return getStageState(this.identity).viewWidth;
    }
    /** 视口高度 */
    getViewHeight() {
        return getStageState(this.identity).viewHeight;
    }
    /** 获取舞台滚动位置 */
    getStageScrollPosition() {
        return getStageState(this.identity).stageScrollPosition;
    }
    /** 缩放比例 */
    getScale() {
        return getStageState(this.identity).scale;
    }
    /** 获取是否自动缩放 */
    getIsAutoScale() {
        return getStageState(this.identity).isAutoScale;
    }
    /** 设置是否自动缩放 */
    setIsAutoScale(isAutoScale: boolean) {
        getStageState(this.identity).setIsAutoScale(isAutoScale);
    }
    /** 选中激活画布，ui 上的选中状态 */
    isPickStage() {
        return getStageState(this.identity).isPickStage;
    }
    /** 舞台是否可见 */
    isStageVisible() {
        return getStageState(this.identity).isStageVisible;
    }
    /** 设置舞台是否可见 */
    setIsStageVisible(isStageVisible: boolean) {
        getStageState(this.identity).setIsStageVisible(isStageVisible);
    }
    /** 当前激活的舞台索引 */
    getActiveStageIndex() {
        return getStageState(this.identity).activeStageIndex;
    }
    /** 当前激活的舞台对应的原始索引（等价于 stage id） */
    getActiveOriginIndex() {
        return getStageState(this.identity).getActiveOriginIndex();
    }
    /** 当前舞台的单位 */
    getStageUnit() {
        return getStageState(this.identity).stageUnit;
    }

    /** 获取视口边距 */
    getViewPortPadding() {
        return getStageState(this.identity).viewPortPadding;
    }
    /** 设置视口边距 */
    setViewPortPadding(viewPortPadding: number) {
        getStageState(this.identity).setViewPortPadding(viewPortPadding);
    }
    /** 设置舞台图层是否隐藏 */
    setIsHideLayers(isHideLayers: boolean) {
        getStageState(this.identity).setIsHideLayers(isHideLayers);
    }
    /** 当前舞台的类型 */
    getStageType() {
        return getStageState(this.identity).stageType;
    }
    /** 当前画布的类型 */
    getCanvasType() {
        return getStageState(this.identity).canvasType;
    }
    /** 设置视口大小 */
    setViewInfo(width: number, height: number) {
        getStageState(this.identity).setViewInfo(width, height);
    }
    /** 设置缩放比例 */
    setScale(scale: number) {
        getStageState(this.identity).setScale(scale);
    }
    /** 设置舞台单位 */
    setStageUnit(unit: SizeUnitType) {
        getStageState(this.identity).setStageUnit(unit);
    }
    /** 获取选中的舞台索引 */
    getStageSelection() {
        return getStageState(this.identity).stageSelection;
    }
    /** 设置选中的舞台索引 */
    setStageSelection(indexes: number[]) {
        getStageState(this.identity).setStageSelection(indexes);
    }
    /** 设置舞台类型 */
    setStageType(stageType: StageType, canvasType: CanvasType) {
        getStageState(this.identity).setStageType(stageType, canvasType);
    }
    /** 设置激活的舞台索引 */
    setActiveStageIndex(stageIndex: number) {
        getStageState(this.identity).setActiveStageIndex(stageIndex);
    }
    /** 根据图层id设置激活的舞台索引 */
    setActiveStageIndexByLayerId(layerId: string) {
        const originIndex = parseLayerId(layerId).originIndex;
        if (isNumber(originIndex)) {
            this.setActiveStageIndex(CoreAPI(this.identity).convertOriginToStage(originIndex));
        }
    }
    /** 设置是否选中激活画布 */
    setIsPickStage(isPickStage: boolean) {
        getStageState(this.identity).setIsPickStage(isPickStage);
    }
    /**
     * 添加或更新图层
     *
     * 手动触发渲染 `getEditorData(this.identity).rerender();`
     *
     * @param props - 图层数据
     * @param isAutoRerender - 可选；是否自动重新渲染；默认值为 `true`，`false` 则需要手动触发渲染
     * @param rerenderOriginIndex - 可选；重新渲染的原始索引，强制刷新整个画布
     */
    addOrUpdate(layer: ILayerProps, isAutoRerender = true, rerenderOriginIndex?: number) {
        return getStageState(this.identity).addOrUpdateLayer(layer, isAutoRerender, rerenderOriginIndex);
    }

    /** 批量更新图层 */
    batchUpdate(layers: ILayerProps[], skipChild?: boolean, autoRerender?: boolean) {
        return getStageState(this.identity).batchUpdateLayer(layers, skipChild, autoRerender);
    }

    /** 替换图层 */
    replaceLayer(
        ids: string | string[],
        newLayers?: ILayerProps | ILayerProps[],
        restoreData?: { index: number; layer: ILayerProps }[],
    ) {
        const oldIds = isArray(ids) ? ids : [ids];
        newLayers = isArray(newLayers) ? newLayers : newLayers ? [newLayers] : undefined;
        return getStageState(this.identity).replaceLayer(oldIds, newLayers, restoreData);
    }

    /** 获取激活舞台的碰撞信息 */
    getShapes(stageIndex: number, deep = false) {
        return getStageState(this.identity).getShapes(stageIndex, deep);
    }

    /** 转换为形状 */
    convertToShapes(layers: ILayerProps[], deep: boolean) {
        return getStageState(this.identity).convertToShapes(layers, deep);
    }

    /** 检测最外层 */
    tryPickWrap(mouseRect: IShape, layers: ILayerProps[]) {
        return getStageState(this.identity).tryPickWrap(mouseRect, layers);
    }

    /** 深度检测子图层 */
    tryPickChild(mouseRect: IShape, layers: ILayerProps[]) {
        return getStageState(this.identity).tryPickChild(mouseRect, layers);
    }

    /** 检测不包括指定的id */
    tryPickWithoutId(mouseRect: IShape, layers: ILayerProps[], withoutId: string) {
        return getStageState(this.identity).tryPickWithoutId(mouseRect, layers, withoutId);
    }

    /** 缩小画布 */
    zoomOut() {
        getStageState(this.identity).zoomOut();
    }

    /** 放大画布 */
    zoomIn() {
        getStageState(this.identity).zoomIn();
    }

    /** 获取激活舞台的尺寸 */
    getActiveStageSize(index?: number) {
        const stageIndex = index === undefined ? this.getActiveStageIndex() : index;
        return CoreAPI(this.identity).getSizeByIndex(stageIndex);
    }

    /**
     * 获取舞台尺寸
     * 单画布：当前激活的尺寸
     * 多画布：总长度
     */
    getSize() {
        if (this.isLongImageMode()) {
            return CoreAPI(this.identity).getLongSize();
        }
        return this.getActiveStageSize();
    }

    /** 是否是多画布 */
    isLongImageMode() {
        return checkIsLongImageMode(getStageState(this.identity).stageType);
    }

    /** 获取当前激活舞台所有图层 */
    getActiveStageLayers() {
        const stageIndex = this.getActiveStageIndex();
        return CoreAPI(this.identity).getLayersByIndex(stageIndex);
    }

    /** 添加舞台 */
    addStages(
        data: ILayerProps[] | ILayerProps[][],
        activeNewStage = false,
        stageIndex?: number,
        scaleWidth: boolean | number = false,
        originIndexes: number[] = [],
    ) {
        return getStageState(this.identity).addStages(data, {
            activeNewStage,
            stageIndex,
            scaleWidth,
            originIndexes,
        });
    }

    /** 替换舞台 */
    replaceActiveStage(data: ILayerProps[], scaleWidth: boolean | number = false, originIndexes: number[] = []) {
        return getStageState(this.identity).addStages(data, {
            replaceActiveStage: true,
            scaleWidth,
            originIndexes,
        });
    }

    /** 替换所有舞台 */
    replaceAllStages(data: ILayerProps[][], originIndexes: number[] = []) {
        return getStageState(this.identity).addStages(data, {
            replaceAllStage: true,
            originIndexes,
        });
    }

    /** 克隆舞台图层 */
    cloneStage(stageIndex: number) {
        return cloneDeep(CoreAPI(this.identity).getLayersByIndex(stageIndex));
    }

    /** 恢复舞台 */
    restoreStage(activeStageIndex: number, stageIndexList: number[]) {
        getStageState(this.identity).restoreStage(activeStageIndex, stageIndexList);
    }

    /** 删除舞台 */
    delStage(stageIndex: number) {
        return getStageState(this.identity).delStage(stageIndex);
    }

    /** 批量删除舞台 */
    batchDelStage(indices: number[]) {
        return getStageState(this.identity).batchDelStage(indices);
    }

    /** 交换舞台 */
    swapStages(stagesIndex: number[], destIndex: number, sourceIndex: number, activeIndex?: number) {
        return getStageState(this.identity).swapStages(stagesIndex, destIndex, sourceIndex, activeIndex);
    }

    /** 隐藏素材层 */
    hideMaterial(disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) {
        getStageState(this.identity).toggleMaterial(true, disableHideFn, originIndex);
    }

    /** 显示素材层 */
    showMaterial(disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) {
        getStageState(this.identity).toggleMaterial(false, disableHideFn, originIndex);
    }
}
